import { useEffect, useRef, type KeyboardEvent } from "react";
import { cn } from "@/lib/cn";

export type MarcaRecorrido = "hecho" | "en_curso" | "pendiente";

export interface PasoRecorridoVista {
  clave: string;
  tipo: "inicio" | "etapa" | "hito" | "fin";
  marca: MarcaRecorrido;
  titulo: string;
  subtitulo: string; // ya formateado: la fecha, el % o "Próximamente"
  porcentaje: number | null; // en curso: dibuja el anillo
  numero: number | null; // etapas pendientes: el número va en el círculo
}

export interface RecorridoProps {
  etiqueta: string;
  pasos: PasoRecorridoVista[];
  seleccionado: string | null;
  actual: string | null; // lleva el cartel "Estamos acá"
  onSeleccionar: (clave: string) => void;
  /** id del panel con el detalle del paso elegido (`role="tabpanel"`), para `aria-controls`. */
  idPanel: string;
}

const TEXTO_MARCA: Record<MarcaRecorrido, string> = { hecho: "Completado", en_curso: "En curso", pendiente: "Pendiente" };
const RADIO = 19;
const CIRCUNFERENCIA = 2 * Math.PI * RADIO;

/** Id del botón de un paso: el panel lo usa en `aria-labelledby`. */
export const idPasoRecorrido = (clave: string) => `recorrido-${clave}`;

function Nodo({ paso }: { paso: PasoRecorridoVista }) {
  if (paso.marca === "en_curso") {
    const pct = Math.min(100, Math.max(0, paso.porcentaje ?? 50));
    return (
      <span className="relative grid size-12 place-items-center rounded-full bg-superficie">
        <span aria-hidden className="absolute inset-0 rounded-full bg-primario-suave motion-safe:animate-ping motion-safe:[animation-duration:2.4s]" />
        <svg aria-hidden viewBox="0 0 44 44" className="absolute inset-0 size-12 -rotate-90">
          <circle cx="22" cy="22" r={RADIO} fill="var(--color-superficie)" stroke="var(--color-primario-suave)" strokeWidth="5" />
          <circle
            cx="22"
            cy="22"
            r={RADIO}
            fill="none"
            stroke="var(--color-primario)"
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={CIRCUNFERENCIA}
            strokeDashoffset={CIRCUNFERENCIA * (1 - pct / 100)}
          />
        </svg>
        <span aria-hidden className="relative text-[0.6875rem] font-semibold text-primario tabular-nums">
          {paso.porcentaje !== null ? `${paso.porcentaje}%` : "●"}
        </span>
      </span>
    );
  }
  const hecho = paso.marca === "hecho";
  const contenido = paso.tipo === "hito" ? "🏁" : paso.tipo === "fin" ? "🔑" : paso.tipo === "inicio" ? "✍" : hecho ? "✓" : paso.numero;
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-12 place-items-center rounded-full text-cuerpo font-semibold",
        hecho ? "bg-exito text-texto-invertido" : "border-2 border-dashed border-borde-fuerte bg-superficie text-texto-suave",
        hecho && (paso.tipo === "hito" || paso.tipo === "fin" || paso.tipo === "inicio") && "bg-exito-suave text-exito ring-2 ring-exito",
      )}
    >
      {contenido}
    </span>
  );
}

/**
 * Recorrido de la obra: una pista horizontal con un paso por etapa grande, unidos por una línea que se pinta hasta
 * donde se llegó. Se desliza con el dedo en el celular. Funciona como pestañas: tocar un paso lo elige y el detalle
 * vive en el panel `idPanel`; con teclado, flechas, Inicio y Fin. El elegido se centra solo.
 */
export function Recorrido({ etiqueta, pasos, seleccionado, actual, onSeleccionar, idPanel }: RecorridoProps) {
  const botones = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    if (!seleccionado) return;
    const quieto = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    botones.current.get(seleccionado)?.scrollIntoView?.({ inline: "center", block: "nearest", behavior: quieto ? "auto" : "smooth" });
  }, [seleccionado]);

  const mover = (e: KeyboardEvent, i: number) => {
    const destino = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: pasos.length - 1 }[e.key];
    if (destino === undefined) return;
    e.preventDefault();
    const paso = pasos[Math.min(pasos.length - 1, Math.max(0, destino))];
    if (!paso) return;
    onSeleccionar(paso.clave);
    botones.current.get(paso.clave)?.focus();
  };

  return (
    <div role="tablist" aria-label={etiqueta} className="-mx-4 flex snap-x snap-mandatory overflow-x-auto px-4 pb-2 [scrollbar-width:thin] sm:mx-0 sm:px-0">
      {pasos.map((p, i) => {
        const elegido = p.clave === seleccionado;
        const siguiente = pasos[i + 1];
        return (
          <button
            key={p.clave}
            ref={(b) => {
              if (b) botones.current.set(p.clave, b);
              else botones.current.delete(p.clave);
            }}
            type="button"
            role="tab"
            id={idPasoRecorrido(p.clave)}
            aria-label={`${p.titulo}. ${TEXTO_MARCA[p.marca]}: ${p.subtitulo}${p.clave === actual ? ". Estamos acá" : ""}`}
            aria-selected={elegido}
            aria-controls={idPanel}
            tabIndex={elegido ? 0 : -1}
            onClick={() => onSeleccionar(p.clave)}
            onKeyDown={(e) => mover(e, i)}
            className={cn(
              "group relative flex w-28 shrink-0 snap-center flex-col items-center gap-2 rounded-tarjeta px-1 pt-8 pb-2 text-center sm:min-w-28 sm:flex-1",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primario",
              elegido ? "bg-primario-suave" : "hover:bg-superficie-2",
            )}
          >
            {p.clave === actual && (
              <span className="absolute top-1.5 rounded-full bg-primario px-2 py-0.5 text-[0.6875rem] font-semibold text-texto-invertido">Estamos acá</span>
            )}
            {i > 0 && (
              <span aria-hidden className={cn("absolute top-[3.5rem] left-0 h-1 w-1/2 -translate-y-1/2", p.marca === "pendiente" ? "bg-borde" : "bg-exito")} />
            )}
            {siguiente && (
              <span aria-hidden className={cn("absolute top-[3.5rem] right-0 h-1 w-1/2 -translate-y-1/2", siguiente.marca === "pendiente" ? "bg-borde" : "bg-exito")} />
            )}
            <span className={cn("relative z-10 rounded-full transition-transform motion-safe:duration-200", elegido && "scale-110 ring-4 ring-primario/30")}>
              <Nodo paso={p} />
            </span>
            <span className={cn("line-clamp-2 text-chico leading-tight", elegido ? "font-semibold text-texto" : "font-medium text-texto", p.marca === "pendiente" && !elegido && "text-texto-suave")}>
              {p.titulo}
            </span>
            <span className={cn("text-[0.75rem] tabular-nums", p.marca === "en_curso" ? "font-semibold text-primario" : "text-texto-suave")}>{p.subtitulo}</span>
          </button>
        );
      })}
    </div>
  );
}
